/** Browser / IANA locale helpers for language + timezone preferences. */

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
];

/** Ordered language candidates from the browser (`en-US` then `en`, …). */
export function detectBrowserLanguageCandidates(): string[] {
  if (typeof navigator === "undefined") return [];
  const raw = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter((v): v is string => Boolean(v && String(v).trim()));

  const out: string[] = [];
  for (const item of raw) {
    const lower = item.trim().toLowerCase();
    if (!lower) continue;
    out.push(lower);
    const base = lower.split("-")[0];
    if (base && base !== lower) out.push(base);
  }
  return [...new Set(out)];
}

/** First enabled Lion language that matches a browser language candidate. */
export function matchBrowserLanguage(
  enabledCodes: string[],
): string | null {
  const enabled = new Set(
    enabledCodes.map((c) => c.trim().toLowerCase()).filter(Boolean),
  );
  for (const cand of detectBrowserLanguageCandidates()) {
    if (enabled.has(cand)) return cand;
  }
  return null;
}

export function detectBrowserTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.trim() ? tz.trim() : null;
  } catch {
    return null;
  }
}

export function listTimeZones(): string[] {
  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof intl.supportedValuesOf === "function") {
      return intl.supportedValuesOf("timeZone");
    }
  } catch {
    // fall through
  }
  return FALLBACK_TIMEZONES;
}

export function formatTimeZoneLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === "timeZoneName")?.value;
    return offset ? `${tz} (${offset})` : tz;
  } catch {
    return tz;
  }
}
